import { uuid4 } from "@sentry/utils";
import { FunctionComponent, useCallback, useContext, useMemo } from "react";
import { createPortal } from "react-dom";
import { ControllerRenderProps, FormProvider, useForm } from "react-hook-form";
import styled from "styled-components";
import { DangerButton, PrimaryButton } from "../../../../../components/button";
import { Icon } from "../../../../../components/icons";
import { GlobalOverlayContext } from "../../../../../components/overlay/context";
import { Tabs } from "../../../../../components/tabs/tabs-page";
import {
  defaultFieldNames,
  useDefaultFieldNames,
} from "../../../../../store/launch-configurations/models/default-fields";
import { LaunchConfigurationsDTO } from "../../../../../store/launch-configurations/models/dto";
import { Field } from "../../../../../store/launch-configurations/models/internal/field";
import { colours } from "../../../../../styles/colours";
import CustomFields from "./custom-field";
import DefaultFields from "./default-field";
import IconButton from "@material-ui/core/IconButton";
import { DragIndicator } from "@material-ui/icons";
import { Draggable } from "react-beautiful-dnd";
const StyledTabs = styled(Tabs)`
  width: 40em;
  max-width: 100%;
`;

type ISingleFieldProps = ControllerRenderProps<
  LaunchConfigurationsDTO.Requests.SetSingle.T,
  `pages.${number}.fields.${number}`
> & {
  remove: () => void;
  index: number;
  id: string;
};

const Container = styled.div`
  background: ${colours.lighterBlue};
  padding: 1em;
  border-radius: 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 0.5em;

  &:hover {
    background: ${colours.lightBlue};
  }
`;

const SingleField: FunctionComponent<ISingleFieldProps> = ({
  index,
  id,
  remove,
  onChange,
  value,
}) => {
  const { getRef, setOverlay, closeOverlay } = useContext(GlobalOverlayContext);

  const form = useForm<Field.Field<string>>({
    defaultValues: value,
  });

  const fieldNameMap = useDefaultFieldNames();

  const uuid = useMemo(() => uuid4(), []);
  const ref = useMemo(() => getRef && getRef(uuid), [uuid, getRef]);

  const tabs = useMemo(
    () => [
      {
        title: "Default Fields",
        content: <DefaultFields />,
      },
      {
        title: "Custom Fields",
        content: <CustomFields />,
      },
    ],
    []
  );

  const onSubmit = useCallback(
    (data: Field.Field<string>) => {
      onChange(data);
      closeOverlay();
    },
    [onChange, closeOverlay]
  );

  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <Container
            onClick={() => {
              setOverlay({
                portalUUID: uuid,
                title: "Field Details",
              });
            }}
          >
            <div style={{ flexGrow: 1 }}>
              {Field.isDefaultField(value)
                ? fieldNameMap[value.field]
                : value?.title["en_GB"]}
              <FormProvider {...form}>
                {ref
                  ? createPortal(
                      <form onSubmit={form.handleSubmit(onSubmit)}>
                        <StyledTabs
                          tabs={tabs}
                          initialTab={
                            ((defaultFieldNames as any) as string[]).includes(
                              value.field
                            )
                              ? 0
                              : 1
                          }
                        />
                        <PrimaryButton type="submit">Save</PrimaryButton>
                      </form>,
                      ref
                    )
                  : null}
              </FormProvider>
            </div>
            <IconButton {...provided.dragHandleProps} aria-label="drag">
              <DragIndicator />
            </IconButton>
            <DangerButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                remove();
              }}
            >
              <Icon type="trash" />
            </DangerButton>
          </Container>
        </div>
      )}
    </Draggable>
  );
};

export default SingleField;
